import { Box, Link, Typography } from "@mui/material";
import React from "react";

import type { RichText as RichTextDoc } from "./contents";

type Mark = { type: string; attrs?: { href?: string } };
type Node = { type: string; text?: string; marks?: Mark[]; content?: Node[] };

const withMark = (children: React.ReactNode, mark: Mark): React.ReactNode => {
    switch (mark.type) {
        case "bold":
            return <strong>{children}</strong>;
        case "italic":
            return <em>{children}</em>;
        case "underline":
            return <u>{children}</u>;
        case "strike":
            return <s>{children}</s>;
        case "code":
            return <code>{children}</code>;
        case "link":
            if (!/^(https?:|mailto:)/i.test(mark.attrs?.href ?? "")) return children;
            return (
                <Link href={mark.attrs?.href} target="_blank" rel="noopener noreferrer">
                    {children}
                </Link>
            );
        default:
            return children;
    }
};

const render = (node: Node, key: number): React.ReactNode => {
    const children = node.content?.map(render);
    switch (node.type) {
        case "text":
            return <React.Fragment key={key}>{(node.marks ?? []).reduce(withMark, node.text)}</React.Fragment>;
        case "paragraph":
            return (
                <Typography key={key} variant="textSm" color="textTextSecondary" component="p" sx={{ m: 0 }}>
                    {children}
                </Typography>
            );
        case "hardBreak":
            return <br key={key} />;
        case "bulletList":
            return (
                <Box key={key} component="ul" sx={{ my: 0, pl: 3 }}>
                    {children}
                </Box>
            );
        case "orderedList":
            return (
                <Box key={key} component="ol" sx={{ my: 0, pl: 3 }}>
                    {children}
                </Box>
            );
        case "listItem":
            return <li key={key}>{children}</li>;
        default:
            return <React.Fragment key={key}>{children}</React.Fragment>;
    }
};

export const RichText: React.FC<{ doc: RichTextDoc }> = ({ doc }) => <Box>{(doc.content as Node[]).map(render)}</Box>;
